import Breadcrumbs from "components/Common/Breadcrumb";
import BadgeForTable from "components/shared-components/BadgeForTable";
import ConfirmModal from "components/shared-components/ConfirmModal";
import CustomDateFilter from "components/shared-components/RemoteTable/custom-date-filter";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import HubFilter from "components/shared-components/RemoteTable/HubFilter/HubFilter";
import {
  CUSTOM_DATE_FILTER_TABLE_TYPE,
  DELAY_FILTER_TIME,
} from "constants/RemoteDataTableContanst";
import {
  useCancelTicket,
  useCreateTicket,
  useLockTicket,
  useProfile,
  useSendToSale,
  useSkipTicket,
  useTicketList,
} from "hooks";
import { getTicketRowImages } from "helpers/ticketResourceImage";
import jwt_decode from "jwt-decode";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  customFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import { AUTH_TOKEN } from "store/auth/login/actionTypes";
import OrderCodeCopy from "pages/ManageOrders/OrderCodeCopy";
import TicketCreateModal from "./TicketCreateModal";
import TicketImagesModal from "./TicketImagesModal";
import TicketSendSaleModal from "./TicketSendSaleModal";
import TicketStatusMultiFilter from "./TicketStatusMultiFilter";
import { useTicketRouteGuard } from "./useTicketRouteGuard";
import {
  formatTicketStatus,
  getInitTicketParams,
  resolveTicketRoleView,
  stripTicketTableColumnFilters,
  normalizeTicketListColumnFilter,
  TICKET_ROLE_VIEW,
  TICKET_ROW_ACTION_STATUSES,
  TICKET_CREATE_ORDER_VISIBLE_STATUSES,
  isTicketLockSuccessful,
  getTicketLockErrorMessage,
  openDeferredPopup,
  isTicketSendToSaleSuccessful,
  getTicketSendToSaleErrorMessage,
  SEND_TO_SALE_VISIBLE_STATUSES,
  TICKET_SALE_FILTER_STATUS_OPTIONS,
  TICKET_SALE_HIDDEN_STATUSES,
  resolveSaleTicketStatusFilter,
  isTicketStatusHiddenForSale,
  ADMIN_CANCEL_TICKET_VISIBLE_STATUSES,
  buildSendToSalePayload,
  resolveSendToSaleOrderRefCode,
  normalizeReceiveBusOrderRefCode,
  resolveTicketDisplayCode,
  formatTicketSaleNamesPreview,
  getTicketSaleNamesList,
} from "./utils";
import "./TicketManagement.scss";

const formatMakerDate = cell => {
  if (cell == null || cell === "") {
    return "";
  }
  const d = new Date(Number(cell) * 1000);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const pad = n => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const TicketManagementPage = () => {
  const { isAllowed, isMyTicketsPath } = useTicketRouteGuard();
  const { t } = useTranslation("ticketManagement");
  const { addToast } = useToasts();
  const navigate = useNavigate();
  const token = localStorage.getItem(AUTH_TOKEN);
  /** @type {Record<string, unknown>} */
  const decodedToken = token ? jwt_decode(token) : {};
  const roles = typeof decodedToken.auth === "string" ? decodedToken.auth.split(",") : [];
  const roleView = useMemo(() => resolveTicketRoleView(roles), [roles]);
  const location = useLocation();
  const ticketBasePath = useMemo(() => {
    const path = String(location?.pathname || "");
    return path.startsWith("/my-tickets") ? "/my-tickets" : "/ticket-management";
  }, [location?.pathname]);
  const isSaleTicketView =
    isMyTicketsPath || roleView === TICKET_ROLE_VIEW.SALE;
  if (!isAllowed) {
    return null;
  }

  const {
    data: profileQuery,
    isFetching: isProfileFetching,
  } = useProfile();

  const profile = profileQuery?.data;
  const currentUserId = useMemo(() => {
    const fromProfileUserId = Number(profile?.user_id);
    if (Number.isFinite(fromProfileUserId) && fromProfileUserId > 0) {
      return fromProfileUserId;
    }
  }, [profile?.user_id, decodedToken]);

  const creatorId = currentUserId;

  const isResolvingCreator =
    currentUserId == null && isProfileFetching;

  const [ticketParams, setTicketParams] = useState(() =>
    getInitTicketParams(isSaleTicketView)
  );
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [openImageTicket, setOpenImageTicket] = useState(null);
  const closeTicketImages = useCallback(() => {
    setOpenImageTicket(null);
  }, []);
  const [sendSaleTicketRow, setSendSaleTicketRow] = useState(null);
  const [skipConfirmTicketRow, setSkipConfirmTicketRow] = useState(null);
  const [adminCancelTicketRow, setAdminCancelTicketRow] = useState(null);
  const latestTicketIds = useRef(new Set());
  const latestListContextRef = useRef("");
  const lastFetchedParamsKeyRef = useRef("");

  const hubIdForCreate = useMemo(() => {
    const raw = ticketParams?.columnFilter?.["hub_id"];
    if (raw == null || raw === "") {
      return undefined;
    }
    const first = String(raw).split(",")[0]?.trim();
    const n = Number(first);
    return Number.isFinite(n) ? n : undefined;
  }, [ticketParams?.columnFilter]);

  const paramsForTicketList = useMemo(() => {
    const cf = { ...(ticketParams.columnFilter || {}) };
    delete cf["filter[view_role]"];
    if (isSaleTicketView && currentUserId != null) {
      cf["recipient_id"] = currentUserId;
      const rawStatusIn = cf["filter[status][in]"];
      const statusInList = Array.isArray(rawStatusIn)
        ? rawStatusIn
        : rawStatusIn
          ? [rawStatusIn]
          : [];

      cf["filter[status][in]"] = resolveSaleTicketStatusFilter(statusInList);
    } else {
      delete cf["recipient_id"];
    }

    delete cf["filter[status][neq]"];

    return { ...ticketParams, columnFilter: cf };
  }, [ticketParams, isSaleTicketView, currentUserId]);

  const listContextKey = useMemo(
    () =>
      JSON.stringify({
        page: paramsForTicketList?.page,
        sizePerPage: paramsForTicketList?.sizePerPage,
        sort: paramsForTicketList?.sort,
        keywords: paramsForTicketList?.keywords,
        columnFilter: paramsForTicketList?.columnFilter,
      }),
    [paramsForTicketList]
  );

  const onFetchTicketsSuccess = useCallback(
    response => {
      const ticketIds = (response.items || []).map(item => item.id);

      if (latestListContextRef.current !== listContextKey) {
        latestListContextRef.current = listContextKey;
        latestTicketIds.current = new Set(ticketIds);
        return;
      }

      if (latestTicketIds.current.size > 0) {
        const newItems = response.items.filter(
          item => !latestTicketIds.current.has(item.id)
        );
        if (newItems.length > 0) {
          const message =
            newItems.length === 1
              ? t("toast.newTicketSingle", {
                label: resolveTicketDisplayCode(newItems[0]),
              })
              : t("toast.newTicketMany", { count: newItems.length });
          addToast(message, { appearance: "info", autoDismiss: true });
        }
      }
      latestTicketIds.current = new Set(ticketIds);
    },
    [t, addToast, listContextKey]
  );

  const onFetchTicketsError = useCallback(() => {
    addToast(t("toast.loadListError"), {
      appearance: "error",
      autoDismiss: true,
    });
  }, [t, addToast]);

  const {
    data: ticketList,
    refetch: fetchTicketList,
    isFetching: isLoadingTicketList,
  } = useTicketList({ params: paramsForTicketList }, onFetchTicketsSuccess, onFetchTicketsError);

  const onCreateTicketSuccess = () => {
    addToast(t("toast.createSuccess"), { appearance: "success", autoDismiss: true });
    setIsOpenCreateModal(false);
    fetchTicketList();
  };

  const onCreateTicketError = () => {
    addToast(t("toast.createError"), { appearance: "error", autoDismiss: true });
  };

  const { mutate: createTicket, isLoading: isCreatingTicket } = useCreateTicket(
    onCreateTicketSuccess,
    onCreateTicketError
  );

  const onSkipTicketSuccess = () => {
    addToast(t("toast.skipSuccess"), { appearance: "success", autoDismiss: true });
    setSkipConfirmTicketRow(null);
    fetchTicketList();
  };

  const { mutate: skipTicket } = useSkipTicket(onSkipTicketSuccess, () => {
    addToast(t("toast.skipError"), { appearance: "error", autoDismiss: true });
  });

  const onCancelTicketSuccess = () => {
    addToast(t("toast.adminCancelSuccess"), { appearance: "success", autoDismiss: true });
    setAdminCancelTicketRow(null);
    fetchTicketList();
  };

  const { mutate: cancelTicket, isLoading: isCancellingTicket } = useCancelTicket(
    onCancelTicketSuccess,
    () => {
      addToast(t("toast.adminCancelError"), { appearance: "error", autoDismiss: true });
    }
  );

  const handleSaleSkipTicket = row => {
    setSkipConfirmTicketRow(row);
  };

  const handleAdminCancelTicket = row => {
    setAdminCancelTicketRow(row);
  };

  const handleAdminCancelConfirm = note => {
    const ticketCode = String(adminCancelTicketRow?.code ?? "").trim();
    const normalizedNote = typeof note === "string" ? note.trim() : "";

    if (ticketCode === "" || normalizedNote === "") {
      return;
    }

    cancelTicket({
      ticket_code: ticketCode,
      note: normalizedNote,
    });
  };

  const { mutate: sendToSale, isLoading: isSendingToSale } = useSendToSale(
    response => {
      if (!isTicketSendToSaleSuccessful(response)) {
        addToast(
          getTicketSendToSaleErrorMessage(response, t("toast.sendToSaleError")),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      addToast(t("toast.sendToSaleSuccess"), {
        appearance: "success",
        autoDismiss: true,
      });
      setSendSaleTicketRow(null);
      fetchTicketList();
    },
    () => {
      addToast(t("toast.sendToSaleError"), {
        appearance: "error",
        autoDismiss: true,
      });
    }
  );

  const { mutate: lockTicket } = useLockTicket(() => {
    fetchTicketList();
  });

  const handleSendSaleConfirm = ({
    ticket: _ticket,
    assignees,
    typeId,
    note,
    resourceIds,
    orderRefCode,
    receiveBusOrderRefCode,
  }) => {
    const ticketId = Number(_ticket?.id);
    if (!Number.isInteger(ticketId) || ticketId <= 0) {
      return;
    }

    sendToSale(
      buildSendToSalePayload({
        ticket_id: ticketId,
        type_id: typeId,
        assignees: Array.isArray(assignees) ? assignees : [],
        note,
        resource_ids: resourceIds,
        order_ref_code: resolveSendToSaleOrderRefCode(_ticket, orderRefCode),
        receive_bus_order_ref_code: normalizeReceiveBusOrderRefCode(
          receiveBusOrderRefCode
        ),
      })
    );
  };

  const handleAdminSendSale = row => {
    setSendSaleTicketRow(row);
  };

  const fetchTicketListRef = useRef(fetchTicketList);
  fetchTicketListRef.current = fetchTicketList;

  useEffect(() => {
    if (!ticketParams?.run) {
      return;
    }
    // `/my-tickets` luôn cần recipient_id để tránh list "tất cả" rồi mới filter.
    if ((isMyTicketsPath || roleView === TICKET_ROLE_VIEW.SALE) && currentUserId == null) {
      return;
    }
    if (
      roleView === TICKET_ROLE_VIEW.ADMIN &&
      !ticketParams?.columnFilter?.["hub_id"]
    ) {
      return;
    }
    if (lastFetchedParamsKeyRef.current === listContextKey) {
      return;
    }
    lastFetchedParamsKeyRef.current = listContextKey;
    fetchTicketListRef.current();
  }, [ticketParams, roleView, currentUserId, listContextKey, isMyTicketsPath]);


  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, columnFilter: tableColumnFilter }
  ) => {
    setTicketParams(prev => {
      const baseColumnFilter = stripTicketTableColumnFilters(prev.columnFilter);
      delete baseColumnFilter.display_name;
      return /** @type {typeof prev} */ ({
        ...prev,
        page: page || prev.page,
        sizePerPage: sizePerPage || prev.sizePerPage,
        sort: sortField ? `${sortOrder}${sortField}` : prev.sort,
        columnFilter: normalizeTicketListColumnFilter({
          ...baseColumnFilter,
          ...(tableColumnFilter || {}),
        }),
      });
    });
  };

  const handleTicketStatusFilterChange = useCallback(
    statuses => {
      setTicketParams(prev => {
        const nextColumnFilter = { ...prev.columnFilter };
        delete nextColumnFilter["filter[status][in]"];

        if (Array.isArray(statuses) && statuses.length > 0) {
          const nextStatuses = isSaleTicketView
            ? statuses.filter(status => !TICKET_SALE_HIDDEN_STATUSES.includes(status))
            : statuses;

          if (nextStatuses.length > 0) {
            nextColumnFilter["filter[status][in]"] = nextStatuses;
          }
        }

        return {
          ...prev,
          page: 1,
          columnFilter: nextColumnFilter,
        };
      });
    },
    [isSaleTicketView]
  );

  const selectedTicketStatuses = useMemo(() => {
    const rawStatusIn = ticketParams?.columnFilter?.["filter[status][in]"];

    if (Array.isArray(rawStatusIn)) {
      return rawStatusIn;
    }

    return rawStatusIn ? [rawStatusIn] : [];
  }, [ticketParams?.columnFilter]);

  const handleSearchAll = useCallback(value => {
    const normalized = typeof value === "string" ? value.trim() : String(value ?? "").trim();

    // Cho phép bấm "Tìm kiếm" gọi lại API dù keywords không đổi (paste đã debounce trước đó).
    lastFetchedParamsKeyRef.current = "";
    setTicketParams(prev => ({
      ...prev,
      page: 1,
      keywords: normalized,
    }));
  }, []);

  const handleCreateTicket = async payload => {
    await createTicket(payload);
  };

  const handleCreateOrder = useCallback(
    row => {
      const ticketId = Number(row?.id);
      if (!Number.isInteger(ticketId) || ticketId <= 0) {
        return;
      }

      const createOrderPath =
        roleView === TICKET_ROLE_VIEW.ADMIN
          ? "/manage-orders/create"
          : "/my-orders/create";
      const createOrderQuery = `${createOrderPath}?ticketId=${ticketId}`;
      const createOrderUrl = `${window.location.origin}${createOrderQuery}`;
      const deferredPopup = openDeferredPopup();

      lockTicket(ticketId, {
        onSuccess: response => {
          if (!isTicketLockSuccessful(response)) {
            deferredPopup.close();
            addToast(getTicketLockErrorMessage(response, t("toast.lockTicketError")), {
              appearance: "error",
              autoDismiss: true,
            });
            return;
          }

          if (deferredPopup.isOpened) {
            deferredPopup.assignUrl(createOrderUrl);
            return;
          }

          navigate(createOrderQuery);
        },
        onError: () => {
          deferredPopup.close();
          addToast(t("toast.lockTicketError"), {
            appearance: "error",
            autoDismiss: true,
          });
        },
      });
    },
    [roleView, lockTicket, addToast, t, navigate]
  );

  const handleViewTicketDetail = row => {
    window.open(`${ticketBasePath}/${row.id}`, "_blank", "noopener,noreferrer");
  };

  const ticketTableItems = useMemo(() => {
    const items = ticketList?.items || [];

    if (!isSaleTicketView) {
      return items;
    }

    return items.filter(row => !isTicketStatusHiddenForSale(row?.status));
  }, [ticketList?.items, isSaleTicketView]);

  const columns = useMemo(() => {
    const page = ticketParams?.page || 1;
    const sizePerPage = ticketParams?.sizePerPage || 10;

    const saleAssigneeColumn = {
      dataField: "display_name",
      text: "Sale & Sales team",
      isDummyField: true,
      sort: false,
      headerStyle: { minWidth: "200px" },
      style: { minWidth: "200px" },
      filter: textFilter({
        delay: DELAY_FILTER_TIME,
        className: "form-control-sm",
        placeholder: "Nhập tên Sale & Sales team",
        style: { height: 34, whiteSpace: "nowrap" },
      }),
      formatter: (_cell, row) => {
        const { text, fullText, isTruncated } = formatTicketSaleNamesPreview(
          getTicketSaleNamesList(row)
        );

        if (!text) {
          return <span className="text-muted">—</span>;
        }

        return (
          <span
            className="ticket-recipient-preview"
            title={isTruncated ? fullText : undefined}
          >
            {text}
          </span>
        );
      },
    };

    const sttColumn = {
      dataField: "stt",
      text: "STT",
      isDummyField: true,
      headerStyle: () => ({ width: "56px", textAlign: "center" }),
      style: { textAlign: "center" },
      formatter: (_cell, _row, rowIndex) =>
        (page - 1) * sizePerPage + rowIndex + 1,
    };

    const codeColumn = {
      dataField: "code",
      text: "Mã ticket",
      sort: true,
      filter: textFilter({
        delay: DELAY_FILTER_TIME,
        className: "form-control-sm",
        placeholder: "Nhập mã ticket",
        style: { height: 34, whiteSpace: "nowrap" },
      }),
      formatter: (_cell, row) => {
        const code = row.code;

        if (!code) {
          return "—";
        }

        return <OrderCodeCopy key={row.id} code={code} />;
      },
    };

    const orderRefCodeColumn = {
      dataField: "order_ref_code",
      text: "Mã tham chiếu",
      sort: true,
      filter: textFilter({
        delay: DELAY_FILTER_TIME,
        className: "form-control-sm",
        placeholder: "Nhập mã đơn",
        style: { height: 34, whiteSpace: "nowrap" },
      }),
      formatter: (cell, row) => {
        if (!cell) {
          return "—";
        }

        return <OrderCodeCopy key={`ref-${row.id}`} code={cell} />;
      },
    };

    const receiveBusOrderRefCodeColumn = {
      dataField: "receive_bus_order_ref_code",
      text: "Mã đơn lấy hàng",
      sort: true,
      filter: textFilter({
        delay: DELAY_FILTER_TIME,
        className: "form-control-sm",
        placeholder: "Nhập mã đơn lấy hàng",
        style: { height: 34, whiteSpace: "nowrap" },
      }),
      formatter: cell => cell || "—",
    };

    const nameColumn = {
      dataField: "name",
      text: "Tên ticket",
      sort: true,
      formatter: (_cell, row) => row.name || "—",
    };

    const statusColumn = {
      dataField: "status",
      text: "Trạng thái",
      sort: true,
      align: "center",
      headerStyle: { width: "160px" },
      style: { minWidth: "160px" },
      formatter: (cell, row) => {
        const rawStatus = row?.status ?? cell;
        const status = formatTicketStatus(rawStatus);
        return (
          <BadgeForTable badgeColor={status.color}>{status.label}</BadgeForTable>
        );
      },
    };

    const noteColumn = {
      dataField: "note",
      text: "Mô tả",
      sort: false,
      headerStyle: { width: "200px", maxWidth: "200px" },
      style: { maxWidth: "200px" },
      formatter: (_cell, row) => {
        const note = String(row?.note ?? _cell ?? "").trim();

        if (!note) {
          return <span className="text-muted">—</span>;
        }

        return (
          <span className="ticket-description-preview" title={note}>
            {note}
          </span>
        );
      },
    };

    const makerDateColumn = {
      dataField: "maker_date",
      text: "Ngày tạo",
      sort: true,
      align: "center",
      headerStyle: { width: "160px" },
      formatter: cell => formatMakerDate(cell),
      filter: customFilter({
        type: CUSTOM_DATE_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => (
        <CustomDateFilter
          onFilter={onFilter}
          column={column}
          paramFilter={ticketParams.columnFilter}
          showOnlyOptions={["clear", "eq"]}
        />
      ),
    };

    const imagesColumn = {
      dataField: "images_preview",
      text: "Hình ảnh",
      isDummyField: true,
      align: "center",
      headerStyle: () => ({ width: "100px" }),
      formatter: (_cell, row) => {
        const images = getTicketRowImages(row);
        if (!images.length) {
          return <span className="text-muted small">—</span>;
        }
        const titleHint =
          images.length > 1
            ? `Xem ảnh đính kèm (${images.length} ảnh)`
            : "Xem ảnh đính kèm";
        return (
          <button
            type="button"
            className="btn btn-link p-0 border-0 bg-transparent"
            onClick={() => setOpenImageTicket(row)}
            title={titleHint}
          >
            <i className="fas fa-image fa-lg text-primary" aria-hidden />
          </button>
        );
      },
    };

    if (isSaleTicketView) {
      return [
        imagesColumn,
        noteColumn,
        makerDateColumn,
        nameColumn,
        codeColumn,
        orderRefCodeColumn,
        statusColumn,
      ];
    }

    return [
      sttColumn,
      codeColumn,
      orderRefCodeColumn,
      receiveBusOrderRefCodeColumn,
      nameColumn,
      statusColumn,
      noteColumn,
      saleAssigneeColumn,
      makerDateColumn,
      imagesColumn,
    ];
  }, [isSaleTicketView, setOpenImageTicket, ticketParams]);

  const listRowAction = useMemo(() => {
    const statusCondition = { status: TICKET_ROW_ACTION_STATUSES };
    const viewDetailAction = {
      icon: (
        <i className="fas fa-eye fa-lg text-info ms-1" title="Xem chi tiết" />
      ),
      text: "Xem chi tiết",
      onClick: row => handleViewTicketDetail(row),
    };
    const createOrderAction = {
      icon: (
        <i className="fas fa-plus-circle fa-lg text-primary ms-1" title="Tạo đơn" />
      ),
      text: "Tạo đơn",
      onClick: row => handleCreateOrder(row),
      conditionVisible: {
        status: TICKET_CREATE_ORDER_VISIBLE_STATUSES,
      },
    };

    if (roleView === TICKET_ROLE_VIEW.SALE) {
      return [
        viewDetailAction,
        createOrderAction,
        {
          icon: (
            <i className="fas fa-ban fa-lg text-danger ms-1" title="Bỏ qua ticket" />
          ),
          text: "Bỏ qua",
          onClick: row => handleSaleSkipTicket(row),
          conditionVisible: statusCondition,
        },
      ];
    }

    return [
      viewDetailAction,
      {
        icon: (
          <i
            className="fas fa-paper-plane fa-lg text-primary ms-1"
            title="Gửi sale"
          />
        ),
        text: "Gửi sale",
        onClick: row => handleAdminSendSale(row),
        conditionVisible: {
          status: SEND_TO_SALE_VISIBLE_STATUSES,
        },
      },
      createOrderAction,
      {
        icon: (
          <i className="fas fa-times-circle fa-lg text-danger ms-1" title="Hủy ticket" />
        ),
        text: "Hủy ticket",
        onClick: row => handleAdminCancelTicket(row),
        conditionVisible: {
          status: ADMIN_CANCEL_TICKET_VISIBLE_STATUSES,
        },
      },
    ];
  }, [
    roleView,
    handleAdminSendSale,
    handleAdminCancelTicket,
    handleCreateOrder,
    handleViewTicketDetail,
    handleSaleSkipTicket,
  ]);

  const ticketToolbar = (
    <div className="d-flex gap-2 align-items-center flex-wrap">
      {roleView === TICKET_ROLE_VIEW.ADMIN && (
        <HubFilter
          queryKey="ticket-management"
          mainFilterParams={ticketParams}
          setMainFilterParams={setTicketParams}
          isHaveAllOption
          isFilterAllDefault
          isNotFilterChildHub
          keyFilterIn="hub_id"
          isCompact
        />
      )}
      <TicketStatusMultiFilter
        selectedStatuses={selectedTicketStatuses}
        onChange={handleTicketStatusFilterChange}
        statusOptions={
          isSaleTicketView ? TICKET_SALE_FILTER_STATUS_OPTIONS : undefined
        }
      />
      {roleView === TICKET_ROLE_VIEW.ADMIN && (
        <Button
          type="button"
          color="primary"
          className="btn-label"
          onClick={() => setIsOpenCreateModal(true)}
        >
          <i className="fas fa-plus label-icon" />
          Tạo ticket
        </Button>
      )}
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content page-ticket-management">
        <MetaTags>
          <title>Quản lý ticket</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs
            title="Ticket"
            heading={`Quản lý ticket (${ticketList?.totalCount || 0})`}
            breadcrumbItem="Quản lý ticket"
          />

          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <RemoteDataTable
                    keyField="id"
                    data={ticketTableItems}
                    columns={columns}
                    page={ticketParams.page}
                    sizePerPage={ticketParams.sizePerPage}
                    totalSize={ticketList?.totalCount || 0}
                    onTableChange={handleTableChange}
                    onSearchAllHandle={handleSearchAll}
                    listMultipleRowAction={[]}
                    listRowAction={listRowAction}
                    loading={
                      isLoadingTicketList ||
                      (roleView === TICKET_ROLE_VIEW.SALE &&
                        currentUserId == null &&
                        isProfileFetching)
                    }
                    isShowRowIndex={false}
                    isShowSearchAll={true}
                    isShowPagination={true}
                  >
                    {ticketToolbar}
                  </RemoteDataTable>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <TicketCreateModal
        isOpen={isOpenCreateModal}
        toggle={() => setIsOpenCreateModal(prev => !prev)}
        onSubmit={handleCreateTicket}
        isSubmitting={isCreatingTicket}
        hubId={hubIdForCreate}
        creatorId={creatorId}
        isResolvingCreator={isResolvingCreator}
      />
      <TicketImagesModal
        isOpen={!!openImageTicket}
        toggle={closeTicketImages}
        ticket={openImageTicket}
      />
      <TicketSendSaleModal
        isOpen={!!sendSaleTicketRow}
        toggle={() => setSendSaleTicketRow(null)}
        ticket={sendSaleTicketRow}
        onConfirm={handleSendSaleConfirm}
        isSubmitting={isSendingToSale}
      />

      <ConfirmModal
        isOpen={!!skipConfirmTicketRow}
        toggle={() => setSkipConfirmTicketRow(null)}
        titleHeader="Xác nhận bỏ qua"
        titleBody="Khi bấm xác nhận, ticket sẽ bị bỏ qua và không thể tiếp tục thao tác. Vui lòng xác nhận thao tác."
        textBtnCancel="Hủy"
        textBtnConfirm="Xác nhận"
        onConfirm={() => {
          const ticketId = Number(skipConfirmTicketRow?.id);
          if (Number.isInteger(ticketId) && ticketId > 0) {
            skipTicket(ticketId);
          }
        }}
      />

      <ConfirmModal
        isOpen={!!adminCancelTicketRow}
        toggle={() => setAdminCancelTicketRow(null)}
        titleHeader={t("confirm.adminCancelTicketTitle")}
        titleBody={t("confirm.adminCancelTicketBody")}
        notePlateHolder={t("confirm.adminCancelReasonPlaceholder")}
        isHaveNote
        isRequireNote
        textBtnCancel={t("sendSale.cancel")}
        textBtnConfirm="Xác nhận"
        isDisableButton={isCancellingTicket}
        onConfirm={handleAdminCancelConfirm}
      />
    </React.Fragment>
  );
};

export default TicketManagementPage;
